import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useTrack } from "../../hooks/useTrack";
import { supabase } from "../../lib/supabase";
import { parseAudioTypes } from "../../data/audioTypes";
import { NewCreativeModal } from "./NewCreativeModal";
import { ReviewCreativeModal } from "./ReviewCreativeModal";
import { EditCreativeModal } from "./EditCreativeModal";
import { formatCreativeDate } from "./creativeTargeting";
import { VariationsPlayer } from "./VariationsPlayer";
import { useCreativeVariationUpdates } from "./useCreativeVariationUpdates";
import { useCreativePhaseChanges } from "./useCreativePhaseChanges";
import { creativePhase, isLiveProcessing } from "./variationState";
import { CREATIVE_CARD_CSS, CreativePhaseBadge, CreativePhaseHint, CreativeWaveform, VISUALLY_HIDDEN } from "./CreativeCardParts";

import { notifyChange } from "../notifications/notify";

// processing_error and processing_started_at come from migration 102.
const VARIATION_COLUMNS = "id, asset_id, asset_uuid, labels, url, status_id, processing_error, processing_started_at, creative_statuses(name, color)";

// New uploads and failures first, so the creative just added is the first card seen.
const PHASE_ORDER = { processing: 0, failed: 1, active: 2, inactive: 3 };

const DATE_CHIP_STYLE = { fontSize:10, padding:"2px 6px", background:"#F5F5F5", color:"#3E3E3E", borderRadius:4 };
const CARD_ACTION_STYLE = { padding:"6px 12px", background:"transparent", color:"#626262", border:"1px solid #E2E2E2", borderRadius:6, fontSize:11, cursor:"pointer" };

// Mirror of `creative_statuses` (id 1=Active, 3=Inactive) used to
// label the markdown diff sent to the CSM. Values match the seed data and
// never change at runtime, so a local constant is cheaper than a join.
const STATUS_NAMES = { 1: "Active", 3: "Inactive" };
const statusName = (id) => STATUS_NAMES[id] ?? `status #${id}`;

function formatCreativeStatusMarkdown({ name, statusId }) {
  return [
    `**Creative:** ${name && String(name).trim() ? name : "(unnamed)"}`,
    `**Status:** ${statusName(statusId)}`,
  ].join("\n");
}

function formatVariationStatusMarkdown({ creativeName, variationLabel, statusId }) {
  return [
    `**Creative:** ${creativeName && String(creativeName).trim() ? creativeName : "(unnamed)"}`,
    `**Variation:** ${variationLabel && String(variationLabel).trim() ? variationLabel : "(unlabeled)"}`,
    `**Status:** ${statusName(statusId)}`,
  ].join("\n");
}

export function CreativeScreen() {
  const { effectiveAdvertiserId: advertiserId, isSupportUser, canManageAdvertiserData } = useAuth();
  const track = useTrack();
  const [creatives, setCreatives] = useState([]);
  const [creativesLoading, setCreativesLoading] = useState(true);
  const [creativesError, setCreativesError] = useState(null);
  const [creativesRefresh, setCreativesRefresh] = useState(0);
  // Which advertiser's creatives are on screen: a re-read of the same list (a live update, a
  // new creative) keeps showing it instead of flashing the loading state.
  const loadedAdvertiserRef = useRef(null);
  const [selected, setSelected]     = useState(null);
  const [reviewCreative, setReviewCreative] = useState(null);
  const [editCreative, setEditCreative] = useState(null);
  const [search, setSearch]         = useState("");
  const [showNewModal, setShowNewModal] = useState(false);
  const audioRef = useRef(null);
  const [playingCreativeId, setPlayingCreativeId] = useState(null);
  const [playingVariationId, setPlayingVariationId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [toggling, setToggling] = useState(new Set());
  const [togglingVariation, setTogglingVariation] = useState(new Set());
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!supabase || advertiserId == null) {
      setCreativesError(advertiserId == null ? "No advertiser linked" : null);
      setCreativesLoading(false);
      return;
    }
    let cancelled = false;
    if (loadedAdvertiserRef.current !== advertiserId) setCreativesLoading(true);
    setCreativesError(null);
    (async () => {
      const { data, error } = await supabase
        .from("creatives")
        .select(`id, name, labels, status_id, duration, script, start_date, end_date, audio_types, creative_statuses(name, color), creative_variations(${VARIATION_COLUMNS})`)
        .eq("advertiser_id", advertiserId)
        .order("created_at", { ascending: false });
      if (cancelled) return;
      if (error && loadedAdvertiserRef.current === advertiserId) {
        // A background re-read failed: keep the list on screen, the next one may succeed.
        console.error("Creatives re-read failed:", error);
      } else if (error) {
        setCreativesError(error.message);
        setCreatives([]);
        loadedAdvertiserRef.current = null;
      } else {
        loadedAdvertiserRef.current = advertiserId;
        setCreatives((data ?? []).map((r) => ({
          id: r.id,
          status_id: r.status_id ?? 3,
          name: r.name ?? null,
          labels: r.labels ?? [],
          duration: r.duration,
          script: r.script,
          start_date: r.start_date,
          end_date: r.end_date,
          asset_id: r.asset_id ?? null,
          audio_types: parseAudioTypes(r.audio_types),
          stored_audio_types: r.audio_types,
          audio_files: (r.creative_variations ?? []).map(v => ({
            url:          v.url ?? null,
            asset_uuid:   v.asset_uuid ?? null,
            processing_error: v.processing_error ?? null,
            processing_started_at: v.processing_started_at ?? null,
            name:         (v.labels ?? []).join(" — ") || r.name || "Unnamed",
            status_id:    v.status_id,
            tag:          v.creative_statuses?.name ?? "Active",
            tagColor:     v.creative_statuses?.color ?? "#F90091",
            variation_id: v.id,
          })),
          tag: r.creative_statuses?.name ?? "Active",
          tagColor: r.creative_statuses?.color ?? "#F90091",
          lift: null,
        })));
      }
      setCreativesLoading(false);
    })();
    return () => { cancelled = true; };
  }, [advertiserId, creativesRefresh]);

  const hasProcessing = useMemo(
    () => creatives.some((c) => c.audio_files.some((v) => isLiveProcessing(v))),
    [creatives],
  );
  // Each card's phase, decided once per read (it depends on the clock only for the
  // processing window, which a re-read refreshes).
  const phases = useMemo(
    () => creatives.map((c) => ({ id: c.id, name: c.name, phase: creativePhase(c) })),
    [creatives],
  );
  const phaseById = useMemo(() => new Map(phases.map((p) => [p.id, p.phase])), [phases]);
  const { justLive, justAdded, announcement } = useCreativePhaseChanges(creativesLoading ? null : phases);
  const refreshCreatives = useCallback(() => setCreativesRefresh((n) => n + 1), []);
  useCreativeVariationUpdates(advertiserId, hasProcessing, refreshCreatives);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) audio.volume = volume;
  }, [volume]);

  useEffect(() => {
    return () => { audioRef.current?.pause(); };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) audioRef.current?.pause();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  const handlePlayVariation = (c, v) => {
    if (!v.url) return;
    const isSameVariation = playingVariationId === v.variation_id;
    if (isSameVariation) {
      if (isPlaying) audioRef.current?.pause();
      else audioRef.current?.play().catch((err) => console.error("Audio resume failed:", err));
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    const audio = new window.Audio(v.url);
    audio.volume = volume;
    audio.addEventListener("timeupdate", () => setCurrentTime(audio.currentTime));
    audio.addEventListener("loadedmetadata", () => setDuration(audio.duration));
    audio.addEventListener("play", () => setIsPlaying(true));
    audio.addEventListener("pause", () => setIsPlaying(false));
    audio.addEventListener("ended", () => {
      setIsPlaying(false);
      setCurrentTime(0);
      setPlayingCreativeId(null);
      setPlayingVariationId(null);
    });
    audioRef.current = audio;
    setPlayingCreativeId(c.id);
    setPlayingVariationId(v.variation_id);
    setCurrentTime(0);
    setDuration(0);
    audio.play().catch((err) => console.error("Audio play failed:", err, "URL:", v.url));
    track('creative_audio_played', { creative_id: c.id, creative_name: c.name, variation_id: v.variation_id });
  };

  const handleSeek = (c, value) => {
    if (!audioRef.current || playingCreativeId !== c.id) return;
    const t = Math.max(0, Math.min(value, duration));
    audioRef.current.currentTime = t;
    setCurrentTime(t);
  };

  const STATUS_META = {
    1: { tag: "Active",   tagColor: "#151515" },
    3: { tag: "Inactive", tagColor: "#555555" },
  };

  const handleToggleActive = async (e, c) => {
    e.stopPropagation();
    if (toggling.has(c.id)) return;
    setToggling(prev => new Set(prev).add(c.id));
    const oldStatusId = c.status_id;
    const newStatusId = oldStatusId === 3 ? 1 : 3;
    setCreatives(prev => prev.map(x => x.id === c.id ? { ...x, status_id: newStatusId, ...STATUS_META[newStatusId] } : x));
    const { error } = await supabase.from("creatives").update({ status_id: newStatusId }).eq("id", c.id);
    if (error) {
      setCreatives(prev => prev.map(x => x.id === c.id ? { ...x, status_id: oldStatusId, ...STATUS_META[oldStatusId] } : x));
    } else {
      // Fire-and-forget CSM notification. Today the toggle UI is admin-only
      // (rendered when `isSupportUser`) and the DB trigger
      // `restrict_creative_status_id_update` enforces the same gate, so this
      // branch never ships in practice — it's wired for the eventual day RLS
      // is relaxed without needing a follow-up code change. The
      // !isSupportUser guard mirrors the pattern used by every other call
      // site so the contract stays uniform.
      if (!isSupportUser) {
        notifyChange({
          supabase,
          eventType: "Creative Gallery - Creative Status",
          advertiserId,
          previousState: formatCreativeStatusMarkdown({ name: c.name, statusId: oldStatusId }),
          newState: formatCreativeStatusMarkdown({ name: c.name, statusId: newStatusId }),
        });
      }
      setCreativesRefresh(r => r + 1);
    }
    setToggling(prev => { const s = new Set(prev); s.delete(c.id); return s; });
  };

  const handleToggleVariation = async (e, c, v) => {
    e.stopPropagation();
    if (togglingVariation.has(v.variation_id)) return;
    setTogglingVariation(prev => new Set(prev).add(v.variation_id));
    const oldStatusId = v.status_id;
    const newStatusId = oldStatusId === 3 ? 1 : 3;
    const isSingleVariation = c.audio_files.length === 1;
    setCreatives(prev => prev.map(x =>
      x.id !== c.id ? x : {
        ...x,
        ...(isSingleVariation ? { status_id: newStatusId, ...STATUS_META[newStatusId] } : {}),
        audio_files: x.audio_files.map(af =>
          af.variation_id === v.variation_id
            ? { ...af, status_id: newStatusId, tag: STATUS_META[newStatusId].tag, tagColor: STATUS_META[newStatusId].tagColor }
            : af
        ),
      }
    ));
    const updates = [
      supabase.from("creative_variations").update({ status_id: newStatusId }).eq("id", v.variation_id),
      ...(isSingleVariation ? [supabase.from("creatives").update({ status_id: newStatusId }).eq("id", c.id)] : []),
    ];
    const results = await Promise.all(updates);
    const hasError = results.some(r => r.error);
    if (hasError) {
      setCreatives(prev => prev.map(x =>
        x.id !== c.id ? x : {
          ...x,
          ...(isSingleVariation ? { status_id: c.status_id, ...STATUS_META[c.status_id] } : {}),
          audio_files: x.audio_files.map(af =>
            af.variation_id === v.variation_id
              ? { ...af, status_id: oldStatusId, tag: STATUS_META[oldStatusId].tag, tagColor: STATUS_META[oldStatusId].tagColor }
              : af
          ),
        }
      ));
    } else if (!isSupportUser) {
      // Single notification per user click even when isSingleVariation also
      // updates the parent creative — the user-perceived action is the
      // variation toggle, so we report at that granularity. Same admin-gate
      // dead-code caveat as handleToggleActive: today the toggle UI and DB
      // RLS are admin-only, so this branch is wired-but-never-fires until
      // the gate is relaxed.
      notifyChange({
        supabase,
        eventType: "Creative Gallery - Variation Status",
        advertiserId,
        previousState: formatVariationStatusMarkdown({
          creativeName: c.name,
          variationLabel: v.name,
          statusId: oldStatusId,
        }),
        newState: formatVariationStatusMarkdown({
          creativeName: c.name,
          variationLabel: v.name,
          statusId: newStatusId,
        }),
      });
    }
    setTogglingVariation(prev => { const s = new Set(prev); s.delete(v.variation_id); return s; });
  };

  const filtered = creatives.filter(c => {
    const searchLower = search.toLowerCase();
    if (!searchLower) return true;
    if (c.name && c.name.toLowerCase().includes(searchLower)) return true;
    if (Array.isArray(c.labels) && c.labels.some(l => l.toLowerCase().includes(searchLower))) return true;
    if (c.tag && c.tag.toLowerCase().includes(searchLower)) return true;
    return false;
  });

  const ACTIVE_LIMIT = 10;
  const sorted = useMemo(
    () => [...filtered].sort((a, b) => PHASE_ORDER[phaseById.get(a.id)] - PHASE_ORDER[phaseById.get(b.id)]),
    [filtered, phaseById]
  );
  // Without Show all: everything but the inactive ones, so a new upload is never hidden.
  const displayed = useMemo(
    () => !showAll && !search ? sorted.filter(c => phaseById.get(c.id) !== "inactive").slice(0, ACTIVE_LIMIT) : sorted,
    [sorted, showAll, search, phaseById]
  );
  const hiddenCount = sorted.length - displayed.length;

  return (
    <div style={{ padding:"28px 28px", maxWidth:900, margin:"0 auto" }}>
      <div role="status" aria-live="polite" style={VISUALLY_HIDDEN}>{announcement}</div>

      <div style={{ display:"flex", alignItems:"center", flexWrap:"wrap", gap:12, marginBottom:20 }}>
        <div style={{ flex:"1 1 240px", background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:10, padding:"10px 14px", display:"flex", gap:10, alignItems:"center", boxShadow:"0 2px 6px rgba(0,0,0,0.06)" }}>
          <span style={{ color:"#7b7b7b", fontSize:14 }}>🔍</span>
          <input
            value={search} onChange={e => { audioRef.current?.pause(); setSearch(e.target.value); }}
            placeholder="Search creatives..."
            style={{ flex:1, background:"transparent", border:"none", outline:"none", color:"#151515", fontSize:13 }}
          />
          {search && <button onClick={() => setSearch("")} style={{ background:"none", border:"none", color:"#7b7b7b", fontSize:16, cursor:"pointer", padding:0, lineHeight:1 }}>✕</button>}
        </div>
        <button onClick={() => { if (!canManageAdvertiserData) return; audioRef.current?.pause(); setShowNewModal(true); track('creative_new_opened'); }} disabled={advertiserId == null || !canManageAdvertiserData} style={{ padding:"8px 16px", background: advertiserId == null || !canManageAdvertiserData ? "#E2E2E2" : "#151515", color: advertiserId == null || !canManageAdvertiserData ? "#B8B8B8" : "#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:600, cursor: advertiserId == null || !canManageAdvertiserData ? "default" : "pointer", whiteSpace:"nowrap", flexShrink:0 }}>+ New Creative</button>
      </div>

      {creativesError && (
        <div style={{ padding:12, marginBottom:16, background:"#FFF5F5", border:"1px solid #fca5a5", borderRadius:10, color:"#e53935", fontSize:13 }}>{creativesError}</div>
      )}
      {creativesLoading ? (
        <div style={{ padding:24, color:"#7b7b7b", fontSize:13 }}>Loading creatives…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding:32, textAlign:"center", color:"#7b7b7b", fontSize:14 }}>
          {creatives.length === 0 ? "No creatives yet. Click + New Creative to add one." : "No creatives match your search."}
        </div>
      ) : (
      <>
      <style>{CREATIVE_CARD_CSS}</style>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(min(100%, 300px), 1fr))", gap:16 }}>
        {displayed.map(c => {
          const isThisPlaying = playingCreativeId === c.id;
          const phase = phaseById.get(c.id);
          const isLive = justLive.has(c.id);
          const dimmed = phase === "inactive";
          return (
          <div key={c.id} onClick={() => {
              const expanding = selected !== c.id;
              setSelected(expanding ? c.id : null);
              audioRef.current?.pause();
              if (expanding) track('creative_expanded', { creative_id: c.id, creative_name: c.name, status: c.tag });
            }}
            className={[isLive && "creative-card--live", justAdded.has(c.id) && "creative-card--arrive"].filter(Boolean).join(" ") || undefined}
            style={{ background:"#FFFFFF", border:`1px solid ${selected===c.id ? "#F90091" : "#E2E2E2"}`, borderRadius:14, padding:18, cursor:"pointer", minWidth:0, overflow:"hidden", transition:"border-color 600ms cubic-bezier(0.16, 1, 0.3, 1)", boxShadow: selected===c.id ? "0 4px 18px rgba(249,0,145,0.12)" : "0 2px 10px rgba(0,0,0,0.06)" }}>
            <CreativeWaveform phase={phase} isPlaying={isThisPlaying && isPlaying} justLive={isLive} />
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:10, opacity: dimmed ? 0.5 : 1 }}>
              <div style={{ minWidth:0, flex:1 }}>
                <div style={{ fontWeight:600, color:"#151515", fontSize:13, marginBottom:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.name || "Unnamed creative"}</div>
                {Array.isArray(c.labels) && c.labels.length > 0 && (
                  <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginBottom:4 }}>
                    {c.labels.map((lbl, i) => (
                      <span key={i} style={{ fontSize:10, padding:"2px 6px", background:"#EEF2FF", color:"#323084", borderRadius:4 }}>{lbl}</span>
                    ))}
                  </div>
                )}
                <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginBottom:4 }}>
                  {Array.isArray(c.audio_types) && c.audio_types.length > 0
                    ? c.audio_types.map(t => {
                        const excl = t.clusivity === "exclude";
                        return (
                          <span key={t.value} style={{ fontSize:10, padding:"2px 6px", background: excl ? "#FFF5F5" : "#F0FFF4", color: excl ? "#e53935" : "#208a3e", borderRadius:4 }}>
                            {excl ? "¬" : ""}{t.display_name}
                          </span>
                        );
                      })
                    : <span style={{ fontSize:10, padding:"2px 6px", background:"#F5F5F5", color:"#7b7b7b", borderRadius:4 }}>All Types</span>
                  }
                  {c.start_date && <span style={DATE_CHIP_STYLE}>Starts {formatCreativeDate(c.start_date)}</span>}
                  {c.end_date && <span style={DATE_CHIP_STYLE}>Ends {formatCreativeDate(c.end_date)}</span>}
                </div>
                <div style={{ fontSize:11, color:"#7b7b7b" }}>~{c.duration} seconds</div>
              </div>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4, flexShrink:0 }}>
                <CreativePhaseBadge key={phase} phase={phase} tag={c.tag} tagColor={c.tagColor} justLive={isLive} />
                {c.audio_files.length === 0 && (
                  <span style={{ fontSize:9, padding:"2px 8px", borderRadius:20, background:"#F5F5F5", color:"#7b7b7b", fontWeight:600, textTransform:"uppercase", letterSpacing:0.5 }}>Pending audio</span>
                )}
              </div>
            </div>
            <CreativePhaseHint phase={phase} />
            {phase !== "processing" && phase !== "failed" && (
              <div style={{ marginTop:8, fontSize:12, color: phase === "active" ? "#F90091" : "#B8B8B8", opacity: dimmed ? 0.5 : 1 }}>{c.lift ?? "—"}</div>
            )}
            {selected===c.id && (
              <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid #E2E2E2" }} onClick={e => e.stopPropagation()}>
                <VariationsPlayer
                  variations={c.audio_files}
                  playingVariationId={playingVariationId}
                  isPlaying={isThisPlaying && isPlaying}
                  currentTime={isThisPlaying ? currentTime : 0}
                  duration={duration || 0}
                  volume={volume}
                  onPlayVariation={(v) => handlePlayVariation(c, v)}
                  onSeek={(t) => handleSeek(c, t)}
                  onVolumeChange={setVolume}
                  isSupportUser={isSupportUser}
                  togglingVariation={togglingVariation}
                  onToggleVariation={(e, v) => handleToggleVariation(e, c, v)}
                />
                <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginTop:10 }}>
                  <button onClick={() => { audioRef.current?.pause(); setReviewCreative(c); track('creative_reviewed', { creative_id: c.id, creative_name: c.name }); }} style={CARD_ACTION_STYLE}>Review</button>
                  {canManageAdvertiserData && phase !== "processing" && !c.audio_files.some((v) => v.url && !v.asset_uuid) && (
                    <button onClick={() => { audioRef.current?.pause(); setEditCreative(c); track('creative_edit_opened', { creative_id: c.id, creative_name: c.name }); }} style={CARD_ACTION_STYLE}>Edit</button>
                  )}
                  {isSupportUser && c.audio_files.length > 0 && (phase === "active" || phase === "inactive") && (
                    <div
                      onClick={(e) => !toggling.has(c.id) && handleToggleActive(e, c)}
                      style={{ display:"flex", alignItems:"center", gap:6, cursor: toggling.has(c.id) ? "default" : "pointer", opacity: toggling.has(c.id) ? 0.5 : 1, flexShrink:0 }}
                    >
                      <div style={{ position:"relative", width:30, height:17, background: c.status_id !== 3 ? "#F90091" : "#E2E2E2", borderRadius:9, transition:"background 0.2s" }}>
                        <div style={{ position:"absolute", top:2, left: c.status_id !== 3 ? 15 : 2, width:13, height:13, background:"#fff", borderRadius:"50%", transition:"left 0.2s" }} />
                      </div>
                      <span style={{ fontSize:9, padding:"2px 7px", borderRadius:20, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5, background: c.status_id === 1 ? "#FFE9F6" : "#F5F5F5", color: c.status_id !== 3 ? "#F90091" : "#B8B8B8" }}>
                        {c.tag}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          );
        })}
      </div>
      {hiddenCount > 0 && (
        <div style={{ textAlign:"center", marginTop:16 }}>
          <button
            onClick={() => setShowAll(true)}
            style={{ background:"none", border:"1px solid #151515", borderRadius:8, color:"#151515", fontSize:13, fontWeight:600, cursor:"pointer", padding:"7px 20px" }}
          >
            {"Show all "}
            <span style={{ fontSize:11, fontWeight:400, opacity:0.7 }}>({sorted.length} creatives)</span>
          </button>
        </div>
      )}
      </>
      )}

      {showNewModal && (
        <NewCreativeModal
          advertiserId={advertiserId}
          canCreate={canManageAdvertiserData}
          onClose={() => setShowNewModal(false)}
          onCreated={() => setCreativesRefresh((r) => r + 1)}
        />
      )}
      {reviewCreative && (
        <ReviewCreativeModal creative={reviewCreative} onClose={() => setReviewCreative(null)} />
      )}
      {editCreative && (
        <EditCreativeModal
          creative={editCreative}
          advertiserId={advertiserId}
          onSaved={refreshCreatives}
          onClose={() => setEditCreative(null)}
        />
      )}
    </div>
  );
}
